import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, ButtonBase, Link } from "@mui/material";
import { brainTokens } from "../../../design/brainTheme";
import { EmptyState, Icon } from "../../../mui";
import {
  getProjectEvents,
  type ProjectEvent,
  type ProjectEventType,
} from "../../../lib/brain-api";

const c = brainTokens.color;
const MONO = brainTokens.font.mono;
const ISSUE_KEY_RE = /\b([A-Z][A-Z0-9]+-\d+)\b/;
const SEVEN_DAYS_MS = 7 * 86400000;
const VIRTUALIZE_AFTER = 200;
const WINDOW_SIZE = 120;
const OVERSCAN = 24;
const ROW_EST = 46;
const DAY_EST = 42;

type FilterId = "all" | "jira" | "session" | "note" | "decision" | "standup";

const FILTERS: { id: FilterId; label: string }[] = [
  { id: "all", label: "All" },
  { id: "jira", label: "Jira" },
  { id: "session", label: "Sessions" },
  { id: "note", label: "Notes & questions" },
  { id: "decision", label: "Decisions" },
  { id: "standup", label: "Standup" },
];

function visitKey(projectId: string): string {
  return `brain.project.visit.${projectId}`;
}

/** Last-visit floor for catch-up (7d fallback). Parent owns stamp lifecycle. */
export function readVisitSince(projectId: string): string {
  return (
    localStorage.getItem(visitKey(projectId)) ??
    new Date(Date.now() - SEVEN_DAYS_MS).toISOString()
  );
}

export function stampVisit(projectId: string): void {
  localStorage.setItem(visitKey(projectId), new Date().toISOString());
}

/** Capture prior visit as the since floor, then stamp now — once per activation wave. */
export function captureCatchUpSince(projectId: string): string {
  const prev = readVisitSince(projectId);
  stampVisit(projectId);
  return prev;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}

function startOfLocalDay(d: Date): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

/** Local calendar day key — stable across label wording (TODAY vs weekday). */
export function dayKeyOf(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "unknown";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDayLabel(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "UNKNOWN";
  const day = startOfLocalDay(d);
  const today = startOfLocalDay(now);
  const yday = today - 86400000;
  const datePart = d
    .toLocaleDateString("en-GB", { day: "numeric", month: "short" })
    .toUpperCase();
  if (day === today) return `TODAY · ${datePart}`;
  if (day === yday) return `YESTERDAY · ${datePart}`;
  const weekday = d.toLocaleDateString("en-GB", { weekday: "short" }).toUpperCase();
  return `${weekday} ${datePart}`;
}

function formatCatchUpSince(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "earlier";
  const time = formatTime(iso);
  if (startOfLocalDay(d) === startOfLocalDay(now)) return `${time} today`;
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return `${time} ${date}`;
}

function splitEventText(e: ProjectEvent): { verb: string; object: string; mono: boolean } {
  if (e.objectKey) {
    const idx = e.text.lastIndexOf(e.objectKey);
    if (idx >= 0) {
      const verb = e.text.slice(0, idx).replace(/\s*[—\-]\s*$/, "").trim();
      return { verb: verb || e.text, object: e.objectKey, mono: true };
    }
  }
  const dash = e.text.match(/^(.*?)\s*[—\-]\s*(.+)$/);
  if (dash) {
    const object = dash[2].trim();
    return { verb: dash[1].trim(), object, mono: ISSUE_KEY_RE.test(object) };
  }
  return { verb: e.text, object: "", mono: false };
}

export type ActivityDayGroup = {
  dayKey: string;
  label: string;
  events: ProjectEvent[];
};

/** Day-group in a selector, not the component render path. */
export function selectActivityDayGroups(
  events: ProjectEvent[],
  opts: { filter: FilterId; catchUp: boolean; sinceIso: string },
): ActivityDayGroup[] {
  let list = events;
  if (opts.catchUp) {
    const sinceMs = Date.parse(opts.sinceIso);
    if (Number.isFinite(sinceMs)) {
      list = list.filter((e) => Date.parse(e.at) >= sinceMs);
    }
  }
  if (opts.filter !== "all") {
    list = list.filter((e) => e.type === opts.filter);
  }

  const groups: ActivityDayGroup[] = [];
  const index = new Map<string, ActivityDayGroup>();
  for (const e of list) {
    const dayKey = dayKeyOf(e.at);
    let g = index.get(dayKey);
    if (!g) {
      g = { dayKey, label: formatDayLabel(e.at), events: [] };
      index.set(dayKey, g);
      groups.push(g);
    }
    g.events.push(e);
  }
  return groups;
}

function countByFilter(events: ProjectEvent[], id: FilterId): number {
  if (id === "all") return events.length;
  return events.filter((e) => e.type === id).length;
}

/** Tinted icon tile per event type — the day-row equivalent of check-in/check-out tiles. */
function eventTint(type: ProjectEventType): { icon: string; bg: string; fg: string } {
  switch (type) {
    case "jira":
      return { icon: "clipboard-list", bg: c.tealTint, fg: c.tealInk };
    case "decision":
      return { icon: "check", bg: c.successBg, fg: c.successText };
    case "note":
      return { icon: "message-circle", bg: c.warnBg, fg: c.warnText };
    case "standup":
      return { icon: "users", bg: c.tealTint2, fg: c.tealInk };
    case "doc":
      return { icon: "book", bg: c.panelBg2, fg: c.muted };
    default:
      return { icon: "clock", bg: c.panelBg2, fg: c.muted };
  }
}

/** Real "days since last seen" per zero-count stream — never fabricated; "none" if it's
 * never happened at all on this project. */
function quietStreams(events: ProjectEvent[], filter: FilterId): Array<{ label: string; value: string }> {
  const streams = FILTERS.filter((f) => f.id !== "all" && f.id !== filter && countByFilter(events, f.id) === 0);
  return streams.map((f) => ({ label: f.label, value: "none" }));
}

function countSince(events: ProjectEvent[], sinceIso: string): number {
  const sinceMs = Date.parse(sinceIso);
  if (!Number.isFinite(sinceMs)) return 0;
  return events.filter((e) => Date.parse(e.at) >= sinceMs).length;
}

type FlatRow =
  | { kind: "day"; key: string; label: string; count: number; height: number }
  | {
      kind: "event";
      key: string;
      event: ProjectEvent;
      height: number;
    };

function flattenGroups(groups: ActivityDayGroup[]): FlatRow[] {
  const out: FlatRow[] = [];
  for (const g of groups) {
    out.push({
      kind: "day",
      key: `day-${g.dayKey}`,
      label: g.label,
      count: g.events.length,
      height: DAY_EST,
    });
    g.events.forEach((e, i) => {
      out.push({
        kind: "event",
        key: `${g.dayKey}-${e.at}-${e.type}-${e.actor}-${i}`,
        event: e,
        height: ROW_EST,
      });
    });
  }
  return out;
}

export function ProjectActivityFeed({
  projectId,
  catchUpActive,
  sinceIso,
  onCatchUpChange,
}: {
  projectId: string;
  catchUpActive: boolean;
  /** Stable since floor owned by the parent — never re-stamped on remount. */
  sinceIso: string;
  onCatchUpChange: (active: boolean) => void;
}) {
  const [events, setEvents] = useState<ProjectEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterId>("all");
  const [windowStart, setWindowStart] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFilter("all");
    setWindowStart(0);
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getProjectEvents(projectId)
      .then((r) => {
        if (!cancelled) setEvents(r.events ?? []);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const groups = useMemo(
    () =>
      selectActivityDayGroups(events, {
        filter,
        catchUp: catchUpActive,
        sinceIso,
      }),
    [events, filter, catchUpActive, sinceIso],
  );

  const flat = useMemo(() => flattenGroups(groups), [groups]);
  const virtualize = flat.length > VIRTUALIZE_AFTER;

  const windowEnd = virtualize
    ? Math.min(flat.length, windowStart + WINDOW_SIZE + OVERSCAN * 2)
    : flat.length;
  const sliceStart = virtualize ? Math.max(0, windowStart) : 0;
  const slice = flat.slice(sliceStart, windowEnd);

  const padTop = useMemo(() => {
    if (!virtualize) return 0;
    let h = 0;
    for (let i = 0; i < sliceStart; i++) h += flat[i]?.height ?? ROW_EST;
    return h;
  }, [virtualize, sliceStart, flat]);

  const padBottom = useMemo(() => {
    if (!virtualize) return 0;
    let h = 0;
    for (let i = windowEnd; i < flat.length; i++) h += flat[i]?.height ?? ROW_EST;
    return h;
  }, [virtualize, windowEnd, flat]);

  const onScroll = useCallback(() => {
    if (!virtualize || !listRef.current) return;
    const top = listRef.current.scrollTop;
    let acc = 0;
    let idx = 0;
    while (idx < flat.length && acc + (flat[idx]?.height ?? ROW_EST) < top) {
      acc += flat[idx]?.height ?? ROW_EST;
      idx++;
    }
    const next = Math.max(0, idx - OVERSCAN);
    setWindowStart((prev) => (prev === next ? prev : next));
  }, [virtualize, flat]);

  useEffect(() => {
    setWindowStart(0);
  }, [filter, catchUpActive, projectId]);

  const catchUpCount = countSince(events, sinceIso);
  const catchUpLabel = catchUpActive
    ? `Showing since your last visit · ${formatCatchUpSince(sinceIso)} — show all`
    : `Since your last visit (${catchUpCount})`;

  const toggleCatchUp = () => onCatchUpChange(!catchUpActive);

  if (loading) {
    return (
      <Box sx={{ pt: "18px", fontSize: 13, color: c.muted2 }}>Loading activity…</Box>
    );
  }

  const quiet = quietStreams(events, filter);

  return (
    <Box sx={{ pt: "18px", display: "flex", gap: "22px", alignItems: "flex-start" }}>
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          {FILTERS.map((f) => {
            const active = filter === f.id;
            const count = countByFilter(events, f.id);
            const empty = count === 0 && f.id !== "all";
            return (
              <Box
                key={f.id}
                component={ButtonBase}
                onClick={() => setFilter(f.id)}
                sx={{
                  fontSize: 12.5,
                  fontFamily: "inherit",
                  fontWeight: active ? 600 : 400,
                  lineHeight: 1.2,
                  padding: active ? "5px 13px" : "4px 12px",
                  borderRadius: `${brainTokens.radius.pill}px`,
                  border: `1px solid ${active ? "transparent" : c.border}`,
                  background: active ? c.ink : c.white,
                  color: active ? c.white : empty ? c.faint : c.ink4,
                }}
              >
                {f.label}{" "}
                <Box
                  component="span"
                  sx={{ fontFamily: MONO, fontSize: 11, opacity: 0.7 }}
                >
                  {count}
                </Box>
              </Box>
            );
          })}
          <Box sx={{ flex: 1 }} />
          <Box
            component={ButtonBase}
            onClick={toggleCatchUp}
            sx={{
              fontSize: 12.5,
              fontFamily: "inherit",
              color: catchUpActive ? c.tealInk : c.muted,
              lineHeight: 1.3,
              textAlign: "right",
            }}
          >
            {catchUpLabel}
          </Box>
        </Box>

        {groups.length === 0 ? (
          <Box sx={{ mt: "20px" }}>
            <EmptyState icon="activity" title="Nothing in this slice">
              Clear the filter to see the full log.
            </EmptyState>
          </Box>
        ) : (
          <Box
            ref={listRef}
            onScroll={virtualize ? onScroll : undefined}
            sx={{
              mt: "14px",
              background: c.white,
              border: `1px solid ${c.border}`,
              borderRadius: `${brainTokens.radius.card}px`,
              overflow: "hidden",
              ...(virtualize
                ? {
                    maxHeight: "calc(100vh - 240px)",
                    overflowY: "auto",
                  }
                : {}),
            }}
          >
            {padTop > 0 && <Box sx={{ height: padTop }} aria-hidden />}
            {slice.map((row) =>
              row.kind === "day" ? (
                <Box
                  key={row.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "11px 20px",
                    background: c.panelBg2,
                    borderBottom: `1px solid ${c.borderLight}`,
                    borderTop: `1px solid ${c.borderLight}`,
                    "&:first-of-type": { borderTop: "none" },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontFamily: MONO,
                      fontSize: 10,
                      letterSpacing: "0.12em",
                      color: c.faint,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.label}
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, color: c.muted2, whiteSpace: "nowrap" }}>
                    {row.count} {row.count === 1 ? "event" : "events"}
                  </Box>
                </Box>
              ) : (
                <EventRow key={row.key} event={row.event} />
              ),
            )}
            {padBottom > 0 && <Box sx={{ height: padBottom }} aria-hidden />}
          </Box>
        )}
      </Box>

      <Box sx={{ width: 320, flex: "none", position: "sticky", top: 16 }}>
        {quiet.length > 0 && (
          <Box
            sx={{
              background: c.white,
              border: `1px solid ${c.border}`,
              borderRadius: `${brainTokens.radius.card}px`,
              padding: "16px 18px",
            }}
          >
            <Box
              sx={{
                fontFamily: MONO,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: ".1em",
                color: c.faint,
                mb: "10px",
              }}
            >
              QUIET STREAMS
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {quiet.map((q) => (
                <Box key={q.label} sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                  <Box sx={{ fontSize: 13, color: c.ink3, flex: 1 }}>{q.label}</Box>
                  <Box sx={{ fontFamily: MONO, fontSize: 11.5, color: c.muted2 }}>{q.value}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function EventRow({ event }: { event: ProjectEvent }) {
  const { verb, object, mono } = splitEventText(event);
  const typeWord = typeLabel(event.type);
  const tint = eventTint(event.type);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "9px 20px",
        borderBottom: `1px solid ${c.borderLight3}`,
        "&:hover": { background: c.panelBg2 },
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "8px",
          background: tint.bg,
          color: tint.fg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
        }}
      >
        <Icon name={tint.icon} size={14} />
      </Box>
      <Box
        component="span"
        sx={{
          fontFamily: MONO,
          fontSize: 11.5,
          color: c.muted2,
          flex: "none",
          width: 44,
          whiteSpace: "nowrap",
        }}
      >
        {formatTime(event.at)}
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: 13,
          color: event.actorKind === "agent" ? c.tealInk : c.ink2,
          flex: "none",
          width: 108,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {event.actor}
      </Box>
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          color: c.muted,
          lineHeight: 1.5,
        }}
      >
        {verb}
        {object ? " " : ""}
        {object ? (
          event.objectUrl ? (
            <Link
              href={event.objectUrl}
              target="_blank"
              rel="noopener"
              underline="hover"
              sx={{
                fontFamily: mono ? MONO : "inherit",
                color: c.tealInk,
                fontSize: 13,
              }}
            >
              {object}
            </Link>
          ) : (
            <Box
              component="span"
              sx={{
                fontFamily: mono ? MONO : "inherit",
                color: c.ink3,
              }}
            >
              {object}
            </Box>
          )
        ) : null}
      </Box>
      <Box component="span" sx={{ fontSize: 11, color: c.faint, flex: "none" }}>
        {typeWord}
      </Box>
    </Box>
  );
}

function typeLabel(t: ProjectEventType): string {
  switch (t) {
    case "jira":
      return "jira";
    case "session":
      return "session";
    case "note":
      return "note";
    case "decision":
      return "decision";
    case "standup":
      return "standup";
    case "doc":
      return "doc";
    default:
      return t;
  }
}
